import { Box, Hidden, Typography } from "@material-ui/core"
import React, { useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"

import {
  useGetAzpEntryEventHistoryQuery,
  useGetAzpEventEntryQuery,
  useGetAzpEventLinkQuery,
} from "../../api/reigniteApi"
import useEventMetadata from "../../hooks/useEventMetadata"
import { getTokenRacerId } from '../../utils/isLoggedIn'
import { pushGTMEvent } from "../../utils/googleEvents"
import { useIsEventLive } from '../../hooks/useIsEventLive'
import { CtLiveApiStatus } from "../../utils/types"
import { useMainStyles } from "./styles"
import { EventCard } from "./EventCard"
import { PageContainer } from "./PageContainer"
import { AthleteCard, FormattedEventEntry } from "./AthleteCard"
import { RaceCard } from "./RaceCard"
import { DivisionsCard } from "./DivisionsCard"
import { EventsHistory } from "./EventsHistory"
import { useMissingData } from "./hooks/useMissingData"
import { EventSponsors } from "./EventSponsors"
import { EntryStatusCard } from "./EntryStatusCard"
import { Loading } from '../../components/shared/Loading'
import { ReigniteEntryRosterSse } from '../eventResults/ReigniteEntryRosterSse'

type AthleteRacePageProps = {
  azp: string,
  azpEntryId?: number
  azpEventId: string
}

const getStatusLabel = (status: CtLiveApiStatus) => {
  switch (status) {
    case CtLiveApiStatus.Withdrawn:
      return 'Withdrawn'
    case CtLiveApiStatus.DidNotStart:
      return 'Did Not Start'
    case CtLiveApiStatus.DidNotFinish:
      return 'Did Not Finish'
    case CtLiveApiStatus.Disqualified:
      return 'Disqualified'
    case CtLiveApiStatus.Unranked:
      return 'Unranked'
    case CtLiveApiStatus.Deferred:
      return 'Deferred'
    case CtLiveApiStatus.Pending:
      return 'Pending'
    case CtLiveApiStatus.NotSelected:
      return 'Not Selected'
    default:
      return status
  }
}

const RESYNC_ENTRY_TIMEOUT = 60000

const AthleteRacePage = ({
  azp,
  azpEntryId,
  azpEventId,
}: AthleteRacePageProps) => {
  const history = useHistory()
  const styles = useMainStyles()
  const tokenRacerId = getTokenRacerId()
  const [reSyncEntry, setReSyncEntry] = useState(false)
  const [entryNotFound, setEntryNotFound] = useState(false)

  const { data: azpEventLinks } = useGetAzpEventLinkQuery({
    azp,
    azpEventId,
  }, {
    skip: !azp || !azpEventId,
  })

  const eventId = azpEventLinks?.athlinksEventId || 0

  const {
    data: event,
    isLoading: isLoadingEvent,
  } = useEventMetadata(eventId)

  const isEventLive = useIsEventLive(eventId)

  const {
    data: azpEntry,
    isError: isAzpEntryError,
    isLoading: isAzpEntryLoading,
    refetch,
  } = useGetAzpEventEntryQuery({
    azp,
    azpEntryId: azpEntryId ?? 0,
    azpEventId,
  }, {
    skip: !azp || !azpEventId || !azpEntryId,
    pollingInterval: isEventLive && !reSyncEntry ? 30000 : 0,
  })

  const shouldRedirectToIRP = !!azpEntry?.hasResult

  const formattedEntry = useMemo<FormattedEventEntry | null>(() => {
    if (!azpEntry) {
      return null
    }

    if (azpEntry.private) {
      return {
        ...azpEntry,
        bib: null,
        displayName: null,
        firstName: null,
        lastName: null,
        statusLabel: getStatusLabel(azpEntry.status),
      }
    }

    const [firstName, lastName] = azpEntry.displayName.split(' ')

    return {
      ...azpEntry,
      firstName,
      lastName,
      statusLabel: getStatusLabel(azpEntry.status),
    }
  }, [azpEntry])

  const {
    data: eventsHistory,
    isLoading: isEventsHistoryLoading,
  } = useGetAzpEntryEventHistoryQuery({ azp: azp, azpEntryId: azpEntryId || 0 }, {
    skip: !azp || !azpEventId,
  })

  useEffect(() => {
    if (reSyncEntry && !isAzpEntryLoading && (!azpEntry || isAzpEntryError)) {
      const timer = setTimeout(() => {
        setEntryNotFound(true)
      }, RESYNC_ENTRY_TIMEOUT)

      return () => clearTimeout(timer)
    } else {
      setEntryNotFound(false)
    }
  }, [azpEntry, isAzpEntryError, isAzpEntryLoading, reSyncEntry])


  useEffect(() => {
    if (isAzpEntryError || !azpEntryId) {
      setReSyncEntry(true)
    } else {
      setReSyncEntry(false)
      setEntryNotFound(false)
    }

  }, [isAzpEntryError, azpEntryId])

  useEffect(() => {
    if (tokenRacerId && azpEntryId) {
      pushGTMEvent({
        event: 'entry_page_load',
        azp,
        azpEntryId,
        azpEventId,
        userId: tokenRacerId,
      })
    }
  }, [azp, azpEntryId, azpEventId, tokenRacerId])

  const raceLink = azpEventLinks?.races.find(x => x.id === azpEntry?.race?.id)
  const athlinksEventCourseId = raceLink?.athlinksEventCourseId || null
  const eventCourse = event.eventCourseMetadata?.find((item) => item.eventCourseId === athlinksEventCourseId)
  const masterId = event.masterId || null

  const { ctLiveRegLink } = useMissingData({ eventId })
  const registrationLink = (event.thirdPartyRegLink || ctLiveRegLink || null) as string | null

  const redirectingParamsExist = !!(masterId && eventId && athlinksEventCourseId && azpEntry?.bib)
  if (shouldRedirectToIRP && redirectingParamsExist) {
    history.replace(`/event/${masterId}/results/Event/${eventId}/Course/${athlinksEventCourseId}/Bib/${azpEntry.bib}`)
  }

  const isAthleteNotFound = !azpEntry && isAzpEntryError

  if (isAthleteNotFound) {
    return (
      <PageContainer
        azpEventId={azpEventId}
        eventId={shouldRedirectToIRP ? null : eventId}
        eventCourseId={athlinksEventCourseId}
        isEventLive={isEventLive}
      >
        <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' style={{ minHeight: '350px' }}>
          {
            entryNotFound ?
              (
                <Box>
                  <Typography className={styles.notFoundMessage} gutterBottom>
                    Not Found
                  </Typography>
                </Box>
              ) :
              (
                <Box>
                  <Box style={{width: '100%', marginTop: 25, height: 70}}>
                    <Loading/>
                  </Box>
                  <Box>
                    <Typography className={styles.notFoundMessage} gutterBottom>
                      Fetching updates
                    </Typography>
                    <Typography className={styles.notFoundMessage} gutterBottom>
                      This page will automatically update
                    </Typography>
                  </Box>
                </Box>
            )
          }
        </Box>
        <ReigniteEntryRosterSse
          azp={azp}
          eventId={azpEventId}
          entryId={azpEntryId}
          refetch={refetch}
          entryNotFound={entryNotFound}
        />
      </PageContainer>
    )
  }

  const isDivisionsAndHistoryVisible = !!formattedEntry?.status && [
    CtLiveApiStatus.Confirmed,
    CtLiveApiStatus.DidNotStart,
    CtLiveApiStatus.DidNotFinish,
    CtLiveApiStatus.Disqualified,
    CtLiveApiStatus.Unranked
  ].includes(formattedEntry.status)

  return (
    <PageContainer
      azpEventId={azpEventId}
      eventId={shouldRedirectToIRP ? null : eventId}
      eventCourseId={athlinksEventCourseId}
      isEventLive={isEventLive}
    >
      <Box className={styles.container}>
        <Box className={styles.leftSide}>
          <AthleteCard
            entry={formattedEntry}
            isLoadingEntry={!azpEventId || isAzpEntryLoading}
          />

          <Hidden smUp>
            <EntryStatusCard
              athlinksEventCourseId={athlinksEventCourseId}
              eventId={eventId}
              formattedEntry={formattedEntry}
              masterId={masterId}
            />
          </Hidden>

          <Hidden smDown>
            <EventSponsors eventId={eventId} masterId={masterId} />
          </Hidden>
        </Box>

        <Box className={styles.rightSide}>
          <EventCard
            event={event}
            isLoadingEvent={isLoadingEvent}
          />

          <RaceCard
            entryFirstName={formattedEntry?.firstName || null}
            eventCourse={eventCourse || null}
            isLoadingEventCourse={!eventCourse}
            registrationLink={event.registration?.published ? registrationLink : null}
          />

          <Hidden smDown>
            <EntryStatusCard
              athlinksEventCourseId={athlinksEventCourseId}
              eventId={eventId}
              formattedEntry={formattedEntry}
              masterId={masterId}
            />
          </Hidden>

          {isDivisionsAndHistoryVisible && (
            <DivisionsCard
              azpEntry={formattedEntry}
              eventCourse={eventCourse || null}
              eventId={eventId}
              isLoadingEntry={!azpEventId || isAzpEntryLoading}
              isLoadingEventCourse={!eventCourse}
              masterId={masterId}
            />
          )}

          {isDivisionsAndHistoryVisible && (
            <EventsHistory
              entryFirstName={formattedEntry.firstName}
              eventCourseStartDateTime={eventCourse?.eventCourseStartDateTime || null}
              eventCourseName={eventCourse?.eventCourseName || null}
              eventsHistory={eventsHistory?.events || []}
              isEventsHistoryLoading={isEventsHistoryLoading || isLoadingEvent}
              isProcessedByInsights={formattedEntry.isProcessedByInsights}
              masterId={masterId}
            />
          )}

          <Hidden mdUp>
            <EventSponsors eventId={eventId} masterId={masterId} />
          </Hidden>
        </Box>
      </Box>
    </PageContainer>
  )
}

export { AthleteRacePage }